import { Injectable } from '@angular/core';

/**
 * Document mime types supported by the viewer
 */
export enum DocumentMimeType {
  PDF = 'application/pdf',
  PNG = 'image/png',
  JPEG = 'image/jpeg',
  JPG = 'image/jpg',
  GIF = 'image/gif',
  SVG = 'image/svg+xml',
  WEBP = 'image/webp'
}

/**
 * Configuration options for document viewing
 */
export interface DocumentViewerOptions {
  /** Whether to open in a new window/tab */
  openInNewWindow?: boolean;
  /** Window name for the new window */
  windowName?: string;
  /** Window features (size, position, etc.) */
  windowFeatures?: string;
  /** Whether to download instead of viewing */
  download?: boolean;
  /** Filename for download */
  downloadFileName?: string;
}

/**
 * Result of document viewing operation
 */
export interface DocumentViewerResult {
  success: boolean;
  error?: string;
  windowReference?: Window | null;
}

/**
 * Service for handling base64-encoded document viewing
 * Supports PDFs and various image formats
 *
 * @example
 * ```typescript
 * constructor(private documentViewer: DocumentViewerService) {}
 *
 * viewDocument(base64Data: string) {
 *   this.documentViewer.viewBase64Document(
 *     base64Data,
 *     DocumentMimeType.PDF,
 *     { openInNewWindow: true }
 *   );
 * }
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class DocumentViewerService {

  constructor() { }

  /**
   * View a base64-encoded document in the browser
   *
   * @param base64Data - The base64-encoded document data (without data URL prefix)
   * @param mimeType - The MIME type of the document
   * @param options - Optional configuration for viewing behavior
   * @returns Result object indicating success/failure
   */
  viewBase64Document(
    base64Data: string,
    mimeType: DocumentMimeType = DocumentMimeType.PDF,
    options: DocumentViewerOptions = {}
  ): DocumentViewerResult {
    try {
      // Validate input
      if (!base64Data || base64Data.trim() === '') {
        return {
          success: false,
          error: 'Base64 data is empty or invalid'
        };
      }

      // Clean base64 data (remove any data URL prefix if present)
      const cleanedBase64 = this.cleanBase64Data(base64Data);

      // Validate base64 format
      if (!this.isValidBase64(cleanedBase64)) {
        return {
          success: false,
          error: 'Invalid base64 format'
        };
      }

      // Create data URL
      const dataUrl = this.createDataUrl(cleanedBase64, mimeType);

      // Handle download vs view
      if (options.download) {
        this.downloadDocument(dataUrl, options.downloadFileName || 'document');
        return { success: true };
      }

      // Open document
      return this.openDocument(dataUrl, options);

    } catch (error) {
      console.error('Error viewing document:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * View a PDF document from base64 data
   * Convenience method for PDF documents
   *
   * @param base64Data - The base64-encoded PDF data
   * @param options - Optional configuration for viewing behavior
   * @returns Result object indicating success/failure
   */
  viewBase64PDF(
    base64Data: string,
    options: DocumentViewerOptions = { openInNewWindow: true }
  ): DocumentViewerResult {
    return this.viewBase64Document(base64Data, DocumentMimeType.PDF, options);
  }

  /**
   * View an image from base64 data
   *
   * @param base64Data - The base64-encoded image data
   * @param mimeType - The image MIME type
   * @param options - Optional configuration for viewing behavior
   * @returns Result object indicating success/failure
   */
  viewBase64Image(
    base64Data: string,
    mimeType: DocumentMimeType,
    options: DocumentViewerOptions = { openInNewWindow: true }
  ): DocumentViewerResult {
    return this.viewBase64Document(base64Data, mimeType, options);
  }

  /**
   * Download a base64-encoded document
   *
   * @param base64Data - The base64-encoded document data
   * @param fileName - The name for the downloaded file
   * @param mimeType - The MIME type of the document
   * @returns Result object indicating success/failure
   */
  downloadBase64Document(
    base64Data: string,
    fileName: string,
    mimeType: DocumentMimeType = DocumentMimeType.PDF
  ): DocumentViewerResult {
    return this.viewBase64Document(base64Data, mimeType, {
      download: true,
      downloadFileName: fileName
    });
  }

  /**
   * Convert base64 data to a Blob
   * Useful for further processing or uploading
   *
   * @param base64Data - The base64-encoded data
   * @param mimeType - The MIME type of the data
   * @returns Blob object or null if conversion fails
   */
  base64ToBlob(base64Data: string, mimeType: string): Blob | null {
    try {
      const cleanedBase64 = this.cleanBase64Data(base64Data);
      const byteCharacters = atob(cleanedBase64);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      return new Blob([byteArray], { type: mimeType });
    } catch (error) {
      console.error('Error converting base64 to blob:', error);
      return null;
    }
  }

  /**
   * Get the file extension from a MIME type
   *
   * @param mimeType - The MIME type
   * @returns File extension (e.g., 'pdf', 'jpg')
   */
  getFileExtensionFromMimeType(mimeType: DocumentMimeType): string {
    const mimeToExtension: Record<string, string> = {
      [DocumentMimeType.PDF]: 'pdf',
      [DocumentMimeType.PNG]: 'png',
      [DocumentMimeType.JPEG]: 'jpg',
      [DocumentMimeType.JPG]: 'jpg',
      [DocumentMimeType.GIF]: 'gif',
      [DocumentMimeType.SVG]: 'svg',
      [DocumentMimeType.WEBP]: 'webp'
    };
    return mimeToExtension[mimeType] || 'bin';
  }

  /**
   * Private method to clean base64 data
   * Removes data URL prefix if present
   */
  private cleanBase64Data(base64Data: string): string {
    // Remove data URL prefix if present (e.g., "data:application/pdf;base64,")
    const base64Prefix = /^data:[^;]+;base64,/;
    return base64Data.replace(base64Prefix, '').trim();
  }

  /**
   * Private method to validate base64 format
   */
  private isValidBase64(base64String: string): boolean {
    try {
      // Check if string is empty
      if (!base64String || base64String.length === 0) {
        return false;
      }

      // Check if string contains only valid base64 characters
      const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
      if (!base64Regex.test(base64String)) {
        return false;
      }

      // Try to decode
      atob(base64String);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Private method to create a data URL from base64 data
   */
  private createDataUrl(base64Data: string, mimeType: string): string {
    return `data:${mimeType};base64,${base64Data}`;
  }

  /**
   * Private method to open a document in browser
   * Uses Blob URL instead of data URL to avoid browser security restrictions
   */
  private openDocument(
    dataUrl: string,
    options: DocumentViewerOptions
  ): DocumentViewerResult {
    const openInNewWindow = options.openInNewWindow ?? true;
    const windowName = options.windowName || '_blank';
    const windowFeatures = options.windowFeatures || '';

    if (openInNewWindow) {
      try {
        // Extract MIME type and base64 data from data URL
        const matches = dataUrl.match(/^data:([^;]+);base64,(.+)$/);
        if (!matches) {
          return {
            success: false,
            error: 'Invalid data URL format'
          };
        }

        const mimeType = matches[1];
        const base64Data = matches[2];

        // Convert to Blob
        const blob = this.base64ToBlob(base64Data, mimeType);
        if (!blob) {
          return {
            success: false,
            error: 'Failed to convert base64 to blob'
          };
        }

        // Create Blob URL
        const blobUrl = URL.createObjectURL(blob);

        // Open in new window
        const windowRef = window.open(blobUrl, windowName, windowFeatures);

        if (!windowRef) {
          // Clean up blob URL if window failed to open
          URL.revokeObjectURL(blobUrl);
          return {
            success: false,
            error: 'Failed to open new window. Popup may be blocked.'
          };
        }

        // Clean up blob URL after a delay to allow the document to load
        // Note: The blob URL will be revoked after 60 seconds
        setTimeout(() => {
          URL.revokeObjectURL(blobUrl);
        }, 60000);

        return {
          success: true,
          windowReference: windowRef
        };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error opening document'
        };
      }
    } else {
      // Open in same window - use blob URL for consistency
      try {
        const matches = dataUrl.match(/^data:([^;]+);base64,(.+)$/);
        if (!matches) {
          return {
            success: false,
            error: 'Invalid data URL format'
          };
        }

        const mimeType = matches[1];
        const base64Data = matches[2];
        const blob = this.base64ToBlob(base64Data, mimeType);

        if (!blob) {
          return {
            success: false,
            error: 'Failed to convert base64 to blob'
          };
        }

        const blobUrl = URL.createObjectURL(blob);
        window.location.href = blobUrl;
        return { success: true };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error opening document'
        };
      }
    }
  }

  /**
   * Private method to download a document
   */
  private downloadDocument(dataUrl: string, fileName: string): void {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = fileName;
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
